import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, AlertCircle } from 'lucide-react';
import { Layout } from '../components/Layout/Layout';
import { useProjectStore } from '../store/projectStore';
import { useAuthStore } from '../store/authStore';
import { ProfileType } from '../types';

export const ProjectEditPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { currentProject, fetchProject, updateProject, isLoading } = useProjectStore();
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    domain: '',
    funding_requested: 0,
    funding_amount: 0
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  useEffect(() => {
    if (id) {
      fetchProject(id);
    }
  }, [id, fetchProject]);

  useEffect(() => {
    if (currentProject) {
      // Parse wizard data from description if it's JSON
      let description = currentProject.description || '';
      try {
        if (description.trim().startsWith('{') && description.trim().endsWith('}')) {
          const wizardData = JSON.parse(description);
          // Extract text answers for editing
          description = Object.entries(wizardData.answers || {})
            .map(([key, value]) => `${key}: ${value}`)
            .join('\n\n');
        }
      } catch (error) {
        // Use description as-is
      }

      setFormData({
        name: currentProject.name,
        description,
        domain: currentProject.domain || '',
        funding_requested: currentProject.funding_requested || 0,
        funding_amount: currentProject.funding_amount || 0
      });
    }
  }, [currentProject]);

  // Check if user is owner
  const isOwner = user?.id === currentProject?.owner_id;
  const canEdit = user && (
    isOwner ||
    [ProfileType.COORDINATEUR, ProfileType.ADMIN].includes(user.profile_type as ProfileType)
  );

  const handleChange = (field: string, value: string | number) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Le nom du projet est requis';
    }

    if (!formData.domain) {
      newErrors.domain = 'Le domaine est requis';
    }

    if (formData.funding_requested < 0) {
      newErrors.funding_requested = 'Le montant doit être positif';
    }

    if (formData.funding_amount < 0) {
      newErrors.funding_amount = 'Le montant doit être positif';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm() || !id || !canEdit) {
      return;
    }

    setIsSaving(true);
    setSaveError('');

    try {
      await updateProject(id, formData);
      navigate(`/projects/${id}`, {
        state: { message: 'Projet modifié avec succès !' }
      });
    } catch (error: any) {
      console.error('Error updating project:', error);
      setSaveError(error.message || 'Erreur lors de la modification du projet');
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="p-6 flex items-center justify-center min-h-96">
          <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  if (!currentProject || !canEdit) {
    return (
      <Layout>
        <div className="p-6">
          <div className="text-center py-12">
            <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Accès refusé</h2>
            <p className="text-gray-600 mb-6">
              Vous n'avez pas les droits pour modifier ce projet.
            </p>
            <button
              onClick={() => navigate('/projects')}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Retour aux projets
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  const domainOptions = [
    { value: 'AGRICULTURE', label: 'Agriculture' },
    { value: 'ARTISANAT', label: 'Artisanat' },
    { value: 'COMMERCE', label: 'Commerce' },
    { value: 'SERVICES', label: 'Services' },
    { value: 'INDUSTRIE', label: 'Industrie' },
    { value: 'TOURISME', label: 'Tourisme' },
    { value: 'CULTURE', label: 'Culture' },
    { value: 'FORMATION', label: 'Formation' },
    { value: 'SOCIAL', label: 'Social' },
    { value: 'ENVIRONNEMENT', label: 'Environnement' }
  ];

  return (
    <Layout>
      <div className="p-6 max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate(`/projects/${id}`)}
            className="flex items-center space-x-2 text-gray-600 hover:text-gray-800 mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Retour au projet</span>
          </button>

          <h1 className="text-3xl font-bold text-gray-900 mb-2">Modifier le projet</h1>
          <p className="text-gray-600">
            Modifiez les informations de votre projet ESS
          </p>
        </div>

        {/* Error Message */}
        {saveError && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center space-x-2">
              <AlertCircle className="w-5 h-5 text-red-600" />
              <p className="text-red-800 font-medium">{saveError}</p>
            </div>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Project Name */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
              Nom du projet *
            </label>
            <input
              type="text"
              id="name"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                errors.name ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="Ex: Ferme Agroécologique en Guadeloupe"
            />
            {errors.name && (
              <p className="mt-1 text-sm text-red-600">{errors.name}</p>
            )}
          </div>

          {/* Domain */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <label htmlFor="domain" className="block text-sm font-medium text-gray-700 mb-2">
              Domaine d'activité *
            </label>
            <select
              id="domain"
              value={formData.domain}
              onChange={(e) => handleChange('domain', e.target.value)}
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                errors.domain ? 'border-red-300' : 'border-gray-300'
              }`}
            >
              <option value="">Sélectionnez un domaine</option>
              {domainOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            {errors.domain && (
              <p className="mt-1 text-sm text-red-600">{errors.domain}</p>
            )}
          </div>

          {/* Description */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
              Description du projet
            </label>
            <textarea
              id="description"
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              rows={6}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Décrivez votre projet, ses objectifs, son impact..."
            />
            <p className="mt-2 text-sm text-gray-500">
              Décrivez en détail votre projet d'Économie Sociale et Solidaire
            </p>
          </div>

          {/* Funding */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Financement</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="funding_requested" className="block text-sm font-medium text-gray-700 mb-2">
                  Montant demandé (€)
                </label>
                <input
                  type="number"
                  id="funding_requested"
                  value={formData.funding_requested}
                  onChange={(e) => handleChange('funding_requested', parseFloat(e.target.value) || 0)}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.funding_requested ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="0"
                  min="0"
                  step="0.01"
                />
                {errors.funding_requested && (
                  <p className="mt-1 text-sm text-red-600">{errors.funding_requested}</p>
                )}
              </div>

              <div>
                <label htmlFor="funding_amount" className="block text-sm font-medium text-gray-700 mb-2">
                  Montant obtenu (€)
                </label>
                <input
                  type="number"
                  id="funding_amount"
                  value={formData.funding_amount}
                  onChange={(e) => handleChange('funding_amount', parseFloat(e.target.value) || 0)}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.funding_amount ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="0"
                  min="0"
                  step="0.01"
                />
                {errors.funding_amount && (
                  <p className="mt-1 text-sm text-red-600">{errors.funding_amount}</p>
                )}
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex items-center justify-end space-x-4">
            <button
              type="button"
              onClick={() => navigate(`/projects/${id}`)}
              className="px-6 py-3 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              disabled={isSaving}
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={isSaving}
              className="flex items-center space-x-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>Enregistrement...</span>
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  <span>Enregistrer les modifications</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </Layout>
  );
};
